/**
 * 充值页。
 *
 * 余额展示 + 金额选择 + 支付方式（微信/支付宝/USDT）+
 * 提交后展示扫码占位（微信/支付宝）或 USDT 收款地址与交易哈希提交 +
 * 订单记录列表。
 *
 * @module pages/RechargePage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SendIcon from '@mui/icons-material/Send';

import PageHeader from '@/components/common/PageHeader';
import StatCard from '@/components/common/StatCard';
import MoneyText from '@/components/common/MoneyText';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import { useAuthStore } from '@/store/authStore';
import { createRecharge, listOrders, submitUsdtTxHash } from '@/services/order';
import type { Order, OrderStatus, PaymentMethod } from '@/types';
import { formatNumber } from '@/utils/format';
import dayjs from 'dayjs';

/** 预设金额。 */
const PRESET_AMOUNTS = ['50', '100', '200', '500'];

/** 演示用 USDT 收款地址（真实环境由系统配置下发）。 */
const USDT_ADDRESS = 'TRxDemoKouDouRelayUSDTAddress0000000001';

/** 支付方式元信息。 */
const METHOD_META: Record<PaymentMethod, { label: string; color: 'info' | 'primary' | 'warning' }> = {
  wechat: { label: '微信', color: 'info' },
  alipay: { label: '支付宝', color: 'primary' },
  usdt: { label: 'USDT', color: 'warning' },
};

/** 订单状态元信息。 */
const ORDER_STATUS_META: Record<OrderStatus, { label: string; color: 'warning' | 'success' | 'error' }> = {
  pending: { label: '待支付', color: 'warning' },
  paid: { label: '已支付', color: 'success' },
  failed: { label: '失败', color: 'error' },
};

/**
 * 充值页组件。
 *
 * @returns 页面元素。
 */
export default function RechargePage(): JSX.Element {
  const balance = useAuthStore((s) => s.user?.balance ?? '0');
  const { show, Toast } = useToast();

  const [amount, setAmount] = useState('100');
  const [method, setMethod] = useState<PaymentMethod>('wechat');
  const [submitting, setSubmitting] = useState(false);
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [txHash, setTxHash] = useState('');

  const { data: orders, loading, refetch } = useApi(() => listOrders({ page: 1, pageSize: 10 }));

  const handleRecharge = async (): Promise<void> => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      show('请输入有效充值金额', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const order = await createRecharge({ amount: value, paymentMethod: method });
      setActiveOrder(order);
      setTxHash('');
      show('订单已创建，请完成支付');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '充值失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUsdtConfirm = async (): Promise<void> => {
    if (!activeOrder) return;
    if (!txHash.trim()) {
      show('请填写交易哈希', 'warning');
      return;
    }
    try {
      await submitUsdtTxHash(activeOrder.orderNo, txHash.trim());
      show('已提交，等待后台确认');
      setActiveOrder(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '提交失败', 'error');
    }
  };

  const orderColumns: Column<Order>[] = [
    { key: 'orderNo', header: '订单号' },
    {
      key: 'paymentMethod',
      header: '支付方式',
      render: (o) => (
        <StatusChip
          label={METHOD_META[o.paymentMethod].label}
          color={METHOD_META[o.paymentMethod].color}
        />
      ),
    },
    { key: 'amount', header: '金额', align: 'right', render: (o) => <MoneyText value={o.amount} /> },
    {
      key: 'status',
      header: '状态',
      render: (o) => (
        <StatusChip
          label={ORDER_STATUS_META[o.status].label}
          color={ORDER_STATUS_META[o.status].color}
        />
      ),
    },
    {
      key: 'createdAt',
      header: '创建时间',
      render: (o) => dayjs(o.createdAt).format('YYYY-MM-DD HH:mm'),
    },
  ];

  return (
    <Box>
      <PageHeader title="充值" subtitle="为账户充值以继续使用 AI 模型调用" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard title="当前余额" value={<MoneyText value={balance} />} color="#4f46e5" />

        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              充值金额
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {PRESET_AMOUNTS.map((a) => (
                <Button
                  key={a}
                  variant={amount === a ? 'contained' : 'outlined'}
                  onClick={() => setAmount(a)}
                >
                  ¥{a}
                </Button>
              ))}
            </Box>
            <TextField
              label="自定义金额（元）"
              type="number"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              支付方式
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={method}
              exclusive
              onChange={(_e, val: PaymentMethod | null) => val && setMethod(val)}
              sx={{ mb: 2, flexWrap: 'wrap' }}
            >
              <ToggleButton value="wechat">微信</ToggleButton>
              <ToggleButton value="alipay">支付宝</ToggleButton>
              <ToggleButton value="usdt">USDT</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting}
              onClick={handleRecharge}
            >
              {submitting ? '处理中...' : `充值 ¥${formatNumber(Number(amount) || 0)}`}
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* 支付指引 */}
      {activeOrder && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              完成支付
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              订单号：{activeOrder.orderNo}　金额：¥
              {formatNumber(Number(activeOrder.amount))}
            </Alert>

            {method === 'usdt' ? (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  请向以下地址转账，并填写交易哈希：
                </Typography>
                <TextField
                  fullWidth
                  value={USDT_ADDRESS}
                  InputProps={{ readOnly: true }}
                  sx={{ my: 1 }}
                />
                <TextField
                  label="交易哈希 (txHash)"
                  fullWidth
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleUsdtConfirm}
                >
                  提交哈希
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    width: 160,
                    height: 160,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                  }}
                >
                  <QrCodeIcon sx={{ fontSize: 64 }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  请使用{METHOD_META[method].label}扫码支付
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        充值订单
      </Typography>
      <DataTable
        columns={orderColumns}
        rows={orders?.list ?? []}
        rowKey={(o) => o.id}
        loading={loading}
        total={orders?.total ?? 0}
        page={0}
        pageSize={10}
      />
      {Toast}
    </Box>
  );
}
